import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import classes from './addRowModalSetupTime.module.scss';

interface FormData {
  roomTypeId: number;
  bottleCountMin: number;
  bottleCountMax: number;
  incomingJobTypeId: number;
  isIncomingJobAndOutgoingJobBottleStockCodeSame: boolean;
  isIncomingJobAndOutgoingJobCapStockCodeSame: boolean;
  cleanTypeId: number;
  setupTimeMinutes: number;
}

interface DropdownOption {
  label: string;
  value: any;
}

interface AddRowModalSetupTimeProps {
  visible: boolean;
  onHide: () => void;
  onSubmit: (data: FormData) => Promise<void>;
  initialValues?: FormData | null;
  isEdit: boolean;
  roomOptions: DropdownOption[];
  cleanTypeOptions: DropdownOption[];
  incomingJobTypeOptions: DropdownOption[];
}

const AddRowModalSetupTime = ({
  visible,
  onHide,
  onSubmit,
  initialValues,
  isEdit,
  roomOptions,
  cleanTypeOptions,
  incomingJobTypeOptions
}: AddRowModalSetupTimeProps) => {
  const [formData, setFormData] = useState<FormData>({
    roomTypeId: 0,
    bottleCountMin: 0,
    bottleCountMax: 0,
    incomingJobTypeId: 0,
    isIncomingJobAndOutgoingJobBottleStockCodeSame: false,
    isIncomingJobAndOutgoingJobCapStockCodeSame: false,
    cleanTypeId: 0,
    setupTimeMinutes: 0
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const modalSessionRef = useRef<number>(0);
  const initializedRef = useRef<boolean>(false);

  useEffect(() => {
    if (visible) {
      // Track modal session - increment when modal opens
      if (!initializedRef.current) {
        modalSessionRef.current += 1;
        initializedRef.current = true;

        // Only update form when modal first opens
        if (initialValues) {
          setFormData({ ...initialValues });
        } else {
          // Reset form for new entry
          setFormData({
            roomTypeId: 0,
            bottleCountMin: 0,
            bottleCountMax: 0,
            incomingJobTypeId: 0,
            isIncomingJobAndOutgoingJobBottleStockCodeSame: false,
            isIncomingJobAndOutgoingJobCapStockCodeSame: false,
            cleanTypeId: 0,
            setupTimeMinutes: 0
          });
        }
        setErrors({});
      }
    } else {
      // Modal closed, reset initialization flag
      initializedRef.current = false;
    }
    // Note: initialValues is intentionally not in dependencies to prevent re-initialization
    // when parent component re-renders after store updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.roomTypeId || formData.roomTypeId === 0) {
      newErrors.roomTypeId = 'Room is required';
    }
    if (!formData.cleanTypeId || formData.cleanTypeId === 0) {
      newErrors.cleanTypeId = 'Clean Type is required';
    }
    if (!formData.incomingJobTypeId || formData.incomingJobTypeId === 0) {
      newErrors.incomingJobTypeId = 'Incoming Job Type is required';
    }

    // Bottle count validation
    if (formData.bottleCountMin < 0) {
      newErrors.bottleCountMin = 'Bottle Count Min cannot be negative';
    }

    if (formData.bottleCountMax <= 0) {
      newErrors.bottleCountMax = 'Bottle Count Max must be greater than 0';
    }

    if (formData.bottleCountMin > formData.bottleCountMax) {
      newErrors.bottleCountMin = 'Bottle Count Min cannot be greater than Bottle Count Max';
    }

    // Setup Time validation
    if (formData.setupTimeMinutes === null || formData.setupTimeMinutes === undefined) {
      newErrors.setupTimeMinutes = 'Setup Time is required';
    } else if (formData.setupTimeMinutes < 0) {
      newErrors.setupTimeMinutes = 'Setup Time cannot be negative';
    } else if (formData.setupTimeMinutes > 720) {
      newErrors.setupTimeMinutes = 'Setup Time cannot exceed 720 minutes (12 hours)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        setIsSubmitting(true);
        await onSubmit(formData);
        // Close modal after store updates complete
        setTimeout(() => {
          onHide();
        }, 100);
      } catch (error) {
        // Don't close modal on error - error will be shown in toast
        if (environment === 'development') {
          console.error('Error submitting form:', error);
        }
        toast.error('Error saving Setup Time!');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData({ ...formData, [field]: value });

    // Clear error when field is updated
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <Dialog
      header={isEdit ? 'Edit Setup Time Configuration' : 'Add New Setup Time Configuration'}
      visible={visible}
      onHide={onHide}
      className={classes.modal}
      style={{ width: '550px' }}
    >
      <div className={classes.formContainer}>
        <div className={classes.formGroup}>
          <label>Room Type</label>
          <Dropdown
            value={formData.roomTypeId}
            options={roomOptions}
            onChange={e => handleInputChange('roomTypeId', e.value)}
            placeholder="Select Room Type"
            className={`${classes.dropdown} ${errors.roomTypeId ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.roomTypeId && <small className="p-error">{errors.roomTypeId}</small>}
        </div>

        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>Bottle Count Min</label>
            <InputText
              value={formData.bottleCountMin.toString()}
              onChange={e =>
                handleInputChange('bottleCountMin', parseInt(e.target.value || '0', 10))
              }
              keyfilter="pint"
              placeholder="Enter min count"
              className={`${classes.inputText} ${errors.bottleCountMin ? 'p-invalid' : ''}`}
              disabled={isSubmitting}
            />
            {errors.bottleCountMin && <small className="p-error">{errors.bottleCountMin}</small>}
          </div>

          <div className={classes.formGroup}>
            <label>Bottle Count Max</label>
            <InputText
              value={formData.bottleCountMax.toString()}
              onChange={e =>
                handleInputChange('bottleCountMax', parseInt(e.target.value || '0', 10))
              }
              keyfilter="pint"
              placeholder="Enter max count"
              className={`${classes.inputText} ${errors.bottleCountMax ? 'p-invalid' : ''}`}
              disabled={isSubmitting}
            />
            {errors.bottleCountMax && <small className="p-error">{errors.bottleCountMax}</small>}
          </div>
        </div>

        <div className={classes.formGroup}>
          <label>Incoming Job Type</label>
          <Dropdown
            value={formData.incomingJobTypeId}
            options={incomingJobTypeOptions}
            onChange={e => handleInputChange('incomingJobTypeId', e.value)}
            placeholder="Select Incoming Job Type"
            className={`${classes.dropdown} ${errors.incomingJobTypeId ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.incomingJobTypeId && (
            <small className="p-error">{errors.incomingJobTypeId}</small>
          )}
        </div>

        <div className={classes.checkboxGroup}>
          <div className={classes.checkbox}>
            <Checkbox
              inputId="sameBottleStockCode"
              checked={formData.isIncomingJobAndOutgoingJobBottleStockCodeSame}
              onChange={e =>
                handleInputChange('isIncomingJobAndOutgoingJobBottleStockCodeSame', e.checked)
              }
              disabled={isSubmitting}
            />
            <label htmlFor="sameBottleStockCode" className={classes.checkboxLabel}>
              Same Bottle Stock Code for Incoming and Outgoing Jobs
            </label>
          </div>

          <div className={classes.checkbox}>
            <Checkbox
              inputId="sameCapStockCode"
              checked={formData.isIncomingJobAndOutgoingJobCapStockCodeSame}
              onChange={e =>
                handleInputChange('isIncomingJobAndOutgoingJobCapStockCodeSame', e.checked)
              }
              disabled={isSubmitting}
            />
            <label htmlFor="sameCapStockCode" className={classes.checkboxLabel}>
              Same Cap Stock Code for Incoming and Outgoing Jobs
            </label>
          </div>
        </div>

        <div className={classes.formGroup}>
          <label>Clean Type</label>
          <Dropdown
            value={formData.cleanTypeId}
            options={cleanTypeOptions}
            onChange={e => handleInputChange('cleanTypeId', e.value)}
            placeholder="Select Clean Type"
            className={`${classes.dropdown} ${errors.cleanTypeId ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.cleanTypeId && <small className="p-error">{errors.cleanTypeId}</small>}
        </div>

        <div className={classes.formGroup}>
          <label>Setup Time (minutes)</label>
          <InputText
            value={formData.setupTimeMinutes.toString()}
            onChange={e =>
              handleInputChange('setupTimeMinutes', parseInt(e.target.value || '0', 10))
            }
            keyfilter="pint"
            placeholder="Enter setup time in minutes"
            className={`${classes.inputText} ${errors.setupTimeMinutes ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.setupTimeMinutes && <small className="p-error">{errors.setupTimeMinutes}</small>}
        </div>
      </div>

      <div className={classes.modalFooter}>
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={onHide}
          severity="danger"
          text
          disabled={isSubmitting}
        />
        <Button
          label={isSubmitting ? 'Saving...' : 'Save'}
          icon={isSubmitting ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
          onClick={handleSubmit}
          className={classes.saveButton}
          disabled={isSubmitting}
        />
      </div>
    </Dialog>
  );
};

export default AddRowModalSetupTime;
